import React, { useState } from "react";
import { Calendar as CalendarIcon, Check, ChevronsUpDown, Info, Loader2, Search } from "lucide-react";
import { endOfDay, format, startOfDay } from "date-fns";
import { cn } from "src/lib/utils";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/*
 * Search bar of the dashboards: device selector + start / end date + search button.
 * Layout (Dashboard.css, .filter-container): one row on desktop, device on its own row on
 * tablets, and on phones device / both dates side by side / full-width "Search" button.
 *
 * props:
 *   devices          [{ key, label }]
 *   selected         label of the selected device ("" = none)
 *   onSelect(label)  called with the chosen device label
 *   devicesLoading   devices are still being fetched
 *   startDate, endDate, onStartDateChange, onEndDateChange
 *   onSearch, searching
 *   pending          the selection differs from the displayed data: highlight Search + show a hint
 *   hasResults       some data is already displayed (changes the hint text)
 */
export default function DashboardFilterBar({
  devices,
  selected,
  onSelect,
  devicesLoading = false,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  searching = false,
  pending = false,
  hasResults = false,
}) {
  const [open, setOpen] = useState(false);
  const today = endOfDay(new Date());
  const noDevices = !devicesLoading && devices.length === 0;

  let triggerText = selected || "Select a device…";
  if (devicesLoading) triggerText = "Loading devices…";
  else if (noDevices) triggerText = "No devices available";

  return (
    <div className="filter-container" role="search">
      <div className="device-selection">
        <div className="device-picker">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                aria-label="Device"
                disabled={devicesLoading || noDevices}
                className={cn("w-full font-normal", !selected && "text-muted-foreground")}
              >
                {devicesLoading && <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />}
                <span className="truncate" title={selected || undefined}>{triggerText}</span>
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            {/* Same width as the trigger, so long names are readable on every screen size */}
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] min-w-[220px] max-w-[calc(100vw-2rem)] p-0 z-[9999]"
            >
              <Command>
                <CommandInput placeholder="Search device…" />
                <CommandList>
                  <CommandEmpty>No devices found</CommandEmpty>
                  <CommandGroup>
                    {devices.map((device) => (
                      <CommandItem
                        key={device.key}
                        value={device.label}
                        onSelect={() => {
                          onSelect(device.label);
                          setOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4 shrink-0", selected === device.label ? "opacity-100" : "opacity-0")} />
                        <span className="truncate" title={device.label}>{device.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="date-selection">
        <DateButton
          label="Start date"
          date={startDate}
          onChange={onStartDateChange}
          // Not in the future and not after the end date
          disabled={(day) => day > today || (endDate && day > endOfDay(endDate))}
        />
        <DateButton
          label="End date"
          date={endDate}
          onChange={onEndDateChange}
          // Not in the future and not before the start date
          disabled={(day) => day > today || (startDate && day < startOfDay(startDate))}
        />
        <Button
          variant="outline"
          className={cn("filter-search-button h-10 z-0", pending && "filter-search-button--pending")}
          onClick={onSearch}
          disabled={searching}
          aria-label={pending ? "Search: apply the new filters" : "Search"}
          aria-describedby={pending ? "filter-pending-hint" : undefined}
          title={selected ? "Load data for the selected device and dates" : "Select a device first"}
        >
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="filter-search-label">{searching ? "Searching…" : "Search"}</span>
        </Button>
      </div>

      {/* The data is only loaded on Search: tell the user when the selection no longer matches it */}
      {pending && (
        <p id="filter-pending-hint" className="filter-pending-hint" role="status">
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
          {hasResults
            ? "The device or dates changed. Click the search button to update the data."
            : "Click the search button to load the data for the selected device and dates."}
        </p>
      )}
    </div>
  );
}

function DateButton({ label, date, onChange, disabled }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={label}
          title={label}
          className={cn("text-left font-normal justify-start", !date && "text-muted-foreground")}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{date ? format(date, "PP") : "Pick a date"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 z-[9999]">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date}
          disabled={disabled}
          onSelect={(day) => {
            if (!day) return; // Clicking the selected day again keeps it
            onChange(day);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
